import { useMemo, useState } from 'react';
import { Box, Flex, IconButton, Switch } from '@chakra-ui/react';
import { Edit } from '@material-ui/icons';

import { TableBase } from '@app/components/common/table/table-base';
import { booleanBodyRender, TableColumn } from '@app/components/common/table';
import { Txt } from '@app/components/common';

import { OutletDistributorForm, OutletDistributorRow } from '../types';
import { OutletDistributorDialog } from '../modals/outlet-distributor';

const sortByGeneral = (rows: OutletDistributorRow[]) => (
  [...rows].sort((a, b) => Number(b.is_general) - Number(a.is_general))
);

const getDisplayRows = (
  distributors: OutletDistributorRow[],
  nextGeneralDistributor?: EntityBase
) => {
  if (!nextGeneralDistributor?.id) {
    return {
      rows: sortByGeneral(distributors),
      previousGeneralId: undefined as number | undefined,
    };
  }

  const nextGeneralId = Number(nextGeneralDistributor.id);
  const currentGeneral = distributors.find((item) => item.is_general);

  if (!currentGeneral || currentGeneral.distributor_id === nextGeneralId) {
    return {
      rows: sortByGeneral(distributors),
      previousGeneralId: undefined as number | undefined,
    };
  }

  const existingRow = distributors.find((item) => item.distributor_id === nextGeneralId);

  return {
    rows: [
      {
        distributor_id: nextGeneralId,
        distributor_name: nextGeneralDistributor.name || existingRow?.distributor_name || '',
        distributor_outlet_id: existingRow?.distributor_outlet_id || '',
        sfa_id: existingRow?.sfa_id || 0,
        is_general: true,
        is_active: true,
        _rowHighlight: 'new-general' as const,
      },
      ...sortByGeneral(distributors),
    ],
    previousGeneralId: currentGeneral.distributor_id,
  };
};

export const OutletDistributorsTable = ({
  distributors,
  isLoading,
  onAddOutletDistributor,
  onUpdateOutletDistributor,
  nextGeneralDistributor,
  readOnly = false,
  showApplyToggle = false,
  applyChanges = true,
  onApplyChangesChange,
}: {
  distributors: OutletDistributorRow[];
  isLoading: boolean;
  onAddOutletDistributor: (item: OutletDistributorForm) => Promise<boolean>;
  onUpdateOutletDistributor: (distributorId: number, item: OutletDistributorForm) => Promise<boolean>;
  nextGeneralDistributor?: EntityBase;
  readOnly?: boolean;
  showApplyToggle?: boolean;
  applyChanges?: boolean;
  onApplyChangesChange?: (value: boolean) => void;
}) => {
  const [showForm, setShowForm] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(-1);

  const { rows: displayRows, previousGeneralId } = useMemo(
    () => getDisplayRows(distributors, nextGeneralDistributor),
    [distributors, nextGeneralDistributor]
  );

  const currentItem = displayRows[currentIndex];
  const isEdit = currentIndex !== -1;

  const onAddClick = () => {
    setShowForm(true);
    setCurrentIndex(-1);
  };

  const currentFormItem: OutletDistributorForm | undefined = currentItem
    ? {
      distributor: {
        id: currentItem.distributor_id,
        name: currentItem.distributor_name,
      },
      distributor_id: currentItem.distributor_id,
      distributor_outlet_id: currentItem.distributor_outlet_id,
      sfa_id: currentItem.sfa_id ?? undefined,
      is_general: currentItem.is_general,
      is_active: currentItem.is_active,
    }
    : undefined;

  const getRowBackgroundColor = (row: OutletDistributorRow) => {
    if (row._rowHighlight === 'new-general') {
      return 'var(--chakra-colors-green-200)';
    }

    if (previousGeneralId && row.distributor_id === previousGeneralId && row.is_general) {
      return 'var(--chakra-colors-red-200)';
    }

    return undefined;
  };

  return (
    <Box mt={-5} marginX={-4}>
      <TableBase
        title="Список дистрибьюторов"
        onCreateClick={!readOnly && onAddClick}
        getRowBackgroundColor={getRowBackgroundColor}
        additionalToolbar={showApplyToggle ? (
          <Flex alignItems="center" gap={2}>
            <Txt color="dark.300">Применить изменения по дистрибьюторам</Txt>
            <Switch
              isChecked={applyChanges}
              onChange={(event) => onApplyChangesChange?.(event.target.checked)}
            />
          </Flex>
        ) : undefined}
        columns={[
          {
            label: 'Дистрибьютор',
            name: 'distributor_name',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (
                value: OutletDistributorRow['distributor_name'],
                { rowData }
              ) => (
                <Box whiteSpace="pre-wrap" style={{ wordBreak: 'break-all' }}>
                  {value?.toString()} [{rowData.distributor_id}]
                </Box>
              ),
            },
          },
          {
            label: 'ID ТТ в системе дистрибьютора',
            name: 'distributor_outlet_id',
            options: { sort: false, filter: false },
          },
          {
            label: 'SFA ID',
            name: 'sfa_id',
            options: { 
              sort: false, filter: false },
          },
          {
            label: 'Основной',
            name: 'is_general',
            options: {
              sort: false,
              filter: false,
              customBodyRender: booleanBodyRender(),
            },
          },
          {
            label: 'Активен',
            name: 'is_active',
            options: {
              sort: false,
              filter: false,
              customBodyRender: booleanBodyRender(),
            },
          },
          ...( !readOnly ? [ {
            label: 'Действия',
            name: 'distributor_id',
            options: {
              sort: false,
              filter: false,
              customBodyRender: (_, { rowIndex, rowData }) => (
                !readOnly && rowData._rowHighlight !== 'new-general' ? (
                  <Flex gap={2} alignItems="center">
                    <IconButton
                      aria-label="edit"
                      onClick={() => {
                        setShowForm(true);
                        setCurrentIndex(rowIndex);
                      }}
                    >
                      <Edit />
                    </IconButton>
                  </Flex>
                ) : null
              ),
            },
          } ] : [] ) as TableColumn<OutletDistributorRow>[],
        ]}
        data={displayRows}
        loading={isLoading}
        paper={false}
      />
      <OutletDistributorDialog
        isVisible={showForm}
        excludedDistributorIds={distributors.map((item) => item.distributor_id)}
        item={currentFormItem}
        isEdit={isEdit}
        onClose={() => {
          setShowForm(false);
          setCurrentIndex(-1);
        }}
        onSave={async (item) => {
          if (isEdit && currentItem) {
            return onUpdateOutletDistributor(currentItem.distributor_id, item);
          }

          return onAddOutletDistributor(item);
        }}
      />
    </Box>
  );
};
